import { Download } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { liveVehicleApi } from '../LiveVehicleLocation/liveVehicleApi';
import { mapTrackerReportToListItem } from '../LiveVehicleLocation/liveVehicleMappers';
import {
  clampDateToToday,
  formatReportWindowTimestamp,
  getTodayISODate,
  getYesterdayISODate,
} from '../../utils/dateHelpers';

const MAX_REPORT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const DATE_INPUT_CLASS =
  "block w-full h-14 pl-4 pr-3.5 py-2.5 bg-neutral-50/80 rounded-[10px] shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] backdrop-blur-[30px] text-lg font-bold font-['Figtree'] leading-snug";

const buildDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const parsed = new Date(`${dateStr}T${timeStr}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateTimeRange = (start, end) => {
  if (!start || !end) return '';
  const dateOpts = { day: '2-digit', month: 'short', year: 'numeric' };
  const timeOpts = { hour: '2-digit', minute: '2-digit' };
  return `${start.toLocaleDateString(undefined, dateOpts)} ${start.toLocaleTimeString(undefined, timeOpts)} – ${end.toLocaleDateString(undefined, dateOpts)} ${end.toLocaleTimeString(undefined, timeOpts)}`;
};

const formatMetric = (value, suffix = '') => {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const num = Number(value);
  const formatted = Number.isInteger(num) ? String(num) : num.toFixed(1);
  return suffix ? `${formatted}${suffix}` : formatted;
};

const getReportErrorMessage = error => {
  const data = error?.responseData;
  if (data?.message) return data.message;
  if (data?.error === 'invalid_window') {
    return data.message || 'Invalid report date range.';
  }
  if (error?.status === 401) {
    return 'Authentication failed. Please sign in again.';
  }
  return error?.message || 'Failed to generate reports.';
};

const LiveVehicleReport = () => {
  useScreenLogging('LiveVehicleReport', {
    component: 'LiveVehicleReport',
  });

  const [startDate, setStartDate] = useState(() => getYesterdayISODate());
  const [startTime, setStartTime] = useState('00:00');
  const [endDate, setEndDate] = useState(() => getTodayISODate());
  const [endTime, setEndTime] = useState('23:59');
  const [reports, setReports] = useState([]);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState('');

  const startDateTime = useMemo(
    () => buildDateTime(startDate, startTime),
    [startDate, startTime]
  );
  const endDateTime = useMemo(
    () => buildDateTime(endDate, endTime),
    [endDate, endTime]
  );

  const formattedRange = useMemo(
    () => formatDateTimeRange(startDateTime, endDateTime),
    [startDateTime, endDateTime]
  );

  const handleGenerateReport = async () => {
    setError(null);
    setInfoMessage('');

    if (!startDate || !startTime || !endDate || !endTime) {
      setError('Please select both start and end date and time.');
      return;
    }

    if (!startDateTime || !endDateTime) {
      setError('Invalid date or time selected.');
      return;
    }

    if (startDateTime >= endDateTime) {
      setError('End date and time must be after the start date and time.');
      return;
    }

    if (endDateTime.getTime() - startDateTime.getTime() > MAX_REPORT_WINDOW_MS) {
      setError('Report window cannot exceed 7 days.');
      return;
    }

    const from = formatReportWindowTimestamp(startDate, startTime);
    const to = formatReportWindowTimestamp(endDate, endTime);

    if (!from || !to) {
      setError('Invalid date or time selected.');
      return;
    }

    setIsGenerating(true);
    setHasGenerated(false);
    setReports([]);

    try {
      const { items = [] } = await liveVehicleApi.getAllLiveVehicles({
        includePosition: false,
      });

      if (!items.length) {
        setReports([]);
        setHasGenerated(true);
        setInfoMessage('No telematics devices found for your tenant.');
        return;
      }

      const results = await Promise.allSettled(
        items.map(device =>
          liveVehicleApi.getDeviceReport(device.terminal_phone, {
            from,
            to,
            includePdf: true,
          })
        )
      );

      const mappedReports = results
        .filter(result => result.status === 'fulfilled' && result.value?.ok)
        .map(result => mapTrackerReportToListItem(result.value));

      const failedCount = results.length - mappedReports.length;

      setReports(mappedReports);
      setHasGenerated(true);

      if (mappedReports.length === 0) {
        setInfoMessage(
          failedCount > 0
            ? 'No reports could be generated for the selected period.'
            : 'No reports found for the selected period.'
        );
      } else if (failedCount > 0) {
        setInfoMessage(
          `Generated ${mappedReports.length} of ${items.length} vehicle reports. Some devices had no data or could not be loaded.`
        );
      }
    } catch (err) {
      console.error('Live vehicle report generation failed:', err);
      setError(getReportErrorMessage(err));
      setReports([]);
      setHasGenerated(false);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadPdf = report => {
    setInfoMessage('');

    if (report?.pdf_download_url) {
      window.open(report.pdf_download_url, '_blank', 'noopener,noreferrer');
      return;
    }

    setInfoMessage('PDF is not available for this report. Try generating the report again.');
  };

  const dateInputClass = isActive =>
    `${DATE_INPUT_CLASS} ${isActive ? 'outline-green-700 text-green-700' : 'outline-white text-neutral-900'}`;

  return (
    <div className="live-vehicle-report-wrapper">
      <div className="live-vehicle-report-surface">
        <div className="mb-6">
          <h1 className="text-black text-3xl font-bold font-['Figtree'] leading-10">
            Live Vehicle Report
          </h1>
          <p className="mt-2 text-sm text-neutral-600">
            Select a date and time range to generate vehicle activity reports.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {infoMessage && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg">
            {infoMessage}
          </div>
        )}

        <div className="date-range-row mb-6">
          <div className="date-range-group">
            <p className="range-group-label">Start</p>
            <div className="range-inputs">
              <div className="range-field">
                <label className="range-field-label" htmlFor="live-vehicle-start-date">
                  Date
                </label>
                <input
                  id="live-vehicle-start-date"
                  type="date"
                  className={dateInputClass(!!startDate)}
                  value={startDate}
                  max={getTodayISODate()}
                  onChange={e => {
                    const safeValue =
                      clampDateToToday(e.target.value) || getYesterdayISODate();
                    setStartDate(safeValue);
                    setHasGenerated(false);
                    setReports([]);
                  }}
                />
              </div>
              <div className="range-field">
                <label className="range-field-label" htmlFor="live-vehicle-start-time">
                  Time
                </label>
                <input
                  id="live-vehicle-start-time"
                  type="time"
                  className={dateInputClass(!!startTime)}
                  value={startTime}
                  onChange={e => {
                    setStartTime(e.target.value);
                    setHasGenerated(false);
                    setReports([]);
                  }}
                />
              </div>
            </div>
          </div>

          <div className="date-range-group">
            <p className="range-group-label">End</p>
            <div className="range-inputs">
              <div className="range-field">
                <label className="range-field-label" htmlFor="live-vehicle-end-date">
                  Date
                </label>
                <input
                  id="live-vehicle-end-date"
                  type="date"
                  className={dateInputClass(!!endDate)}
                  value={endDate}
                  max={getTodayISODate()}
                  onChange={e => {
                    const safeValue =
                      clampDateToToday(e.target.value) || getTodayISODate();
                    setEndDate(safeValue);
                    setHasGenerated(false);
                    setReports([]);
                  }}
                />
              </div>
              <div className="range-field">
                <label className="range-field-label" htmlFor="live-vehicle-end-time">
                  Time
                </label>
                <input
                  id="live-vehicle-end-time"
                  type="time"
                  className={dateInputClass(!!endTime)}
                  value={endTime}
                  onChange={e => {
                    setEndTime(e.target.value);
                    setHasGenerated(false);
                    setReports([]);
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {formattedRange && (
          <p className="mb-4 text-sm text-neutral-600">
            Report period: <span className="font-semibold text-neutral-800">{formattedRange}</span>
          </p>
        )}

        <button
          type="button"
          className="generate-btn mb-6"
          onClick={handleGenerateReport}
          disabled={isGenerating}
        >
          {isGenerating ? 'Generating…' : 'Generate Report'}
        </button>

        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-neutral-800">Reports</h2>
          {hasGenerated && (
            <span className="text-sm text-neutral-500">
              {reports.length} vehicle{reports.length === 1 ? '' : 's'}
            </span>
          )}
        </div>

        <div className="report-list">
          {!hasGenerated ? (
            <div className="empty-state">
              Select a date range and click Generate Report to view vehicle reports.
            </div>
          ) : isGenerating ? (
            <div className="empty-state">Generating reports…</div>
          ) : reports.length === 0 ? (
            <div className="empty-state">No reports found for the selected period.</div>
          ) : (
            reports.map(report => (
              <div key={report.id} className="vehicle-report-card">
                <div className="vehicle-report-details">
                  <p className="vehicle-report-title">{report.vehicle_name}</p>
                  <p className="vehicle-report-meta">
                    {report.license_plate} · Tracker {report.tracker_serial}
                  </p>
                  <p className="vehicle-report-meta">
                    {formatMetric(report.distance_km, ' km')} · Avg{' '}
                    {formatMetric(report.avg_speed_kmh, ' km/h')}
                    {report.trip_count != null
                      ? ` · ${formatMetric(report.trip_count)} trips`
                      : ''}
                  </p>
                  <p className="vehicle-report-file">{report.file_name}</p>
                </div>
                <button
                  type="button"
                  className={`download-btn${report.pdf_download_url ? '' : ' download-btn--placeholder'}`}
                  title={
                    report.pdf_download_url
                      ? 'Download PDF report'
                      : 'PDF not available'
                  }
                  aria-label={`Download report for ${report.license_plate}`}
                  onClick={() => handleDownloadPdf(report)}
                  disabled={!report.pdf_download_url}
                >
                  <Download className="w-5 h-5 text-white" />
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <style>{`
        .live-vehicle-report-wrapper {
          padding: 1rem;
          width: 100%;
        }

        .live-vehicle-report-surface {
          max-width: 960px;
        }

        .date-range-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1.5rem;
        }

        .date-range-group {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .range-group-label {
          font-size: 0.875rem;
          font-weight: 700;
          color: #374151;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }

        .range-inputs {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0.75rem;
        }

        .range-field {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .range-field-label {
          font-size: 0.8125rem;
          font-weight: 600;
          color: #52607a;
        }

        .generate-btn {
          padding: 0.875rem 1.75rem;
          border-radius: 12px;
          background: linear-gradient(135deg, #1c60ff, #0657d3);
          color: #fff;
          font-weight: 600;
          font-size: 1rem;
          box-shadow: 0 12px 24px rgba(6, 87, 211, 0.25);
          border: none;
          cursor: pointer;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }

        .generate-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 14px 28px rgba(6, 87, 211, 0.3);
        }

        .generate-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .report-list {
          max-height: 60vh;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .vehicle-report-card {
          background: #ffffff;
          border-radius: 16px;
          padding: 1.25rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
          border: 1px solid rgba(226, 232, 240, 0.9);
        }

        .vehicle-report-details {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          min-width: 0;
        }

        .vehicle-report-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #0f172a;
        }

        .vehicle-report-meta {
          font-size: 0.95rem;
          color: #62708b;
        }

        .vehicle-report-file {
          font-size: 0.875rem;
          color: #94a3b8;
          margin-top: 0.25rem;
        }

        .download-btn {
          width: 48px;
          height: 48px;
          flex-shrink: 0;
          border-radius: 12px;
          background: linear-gradient(135deg, #1c60ff, #0657d3);
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 15px 25px rgba(6, 87, 211, 0.25);
          border: none;
          cursor: pointer;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }

        .download-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 18px 28px rgba(6, 87, 211, 0.3);
        }

        .download-btn:disabled,
        .download-btn--placeholder {
          opacity: 0.45;
          cursor: not-allowed;
        }

        .empty-state {
          width: 100%;
          text-align: center;
          padding: 2rem 0;
          color: #6b7280;
          font-weight: 500;
        }

        @media (max-width: 768px) {
          .date-range-row {
            grid-template-columns: 1fr;
          }

          .range-inputs {
            grid-template-columns: 1fr;
          }

          .vehicle-report-card {
            flex-direction: column;
            align-items: flex-start;
          }

          .download-btn {
            align-self: flex-end;
          }
        }
      `}</style>
    </div>
  );
};

export default LiveVehicleReport;
